import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  PERSON_SELECT_NOT_LISTED,
  Switch,
  buttonVariants,
} from '@wundo/ui';
import { SaveStatus } from '@/components/SaveStatus.tsx';
import { ContactOption } from '@/components/onboarding/ContactOption.tsx';
import { ManagerSelect } from '@/components/team/ManagerSelect.tsx';
import { useManagerAnswer } from '@/components/team/useManagerAnswer.ts';
import { useFirstCalendarSync } from '@/components/onboarding/useFirstCalendarSync.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { fetchOneOnOneColleagues, type OneOnOneColleagueItem } from '@/lib/colleagues.ts';
import {
  addFollowException,
  fetchFollowExceptions,
  fetchFollowPreferences,
  removeFollowException,
  saveFollowPreferences,
} from '@/lib/meetingFollow.ts';
import { followCorrectionFor } from '@/lib/personCorrections.ts';
import { helpUrl } from '@wundo/shared/helpUrl.ts';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';

/** As many people as read at a glance. The rest are added on the agenda. */
const SHOWN = 5;

/* Module-level, so the sync watch and the invalidations keep one identity. */
const COLLEAGUES_KEY = ['oneOnOneColleagues'];
const EXCEPTIONS_KEY = ['meetingFollowExceptions'];

/**
 * The last screen of the calendar branch: whose one-to-ones Wundo prepares.
 *
 * A pick is the standing correction for that person, stored the moment it is
 * made — the kinds are not asked here, so following comes from these picks and
 * nothing else.
 */
export const Route = createFileRoute('/onboarding/colleagues')({
  component: OnboardingColleaguesPage,
});

export function OnboardingColleaguesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const sync = useFirstCalendarSync(COLLEAGUES_KEY);

  const colleagues = useQuery({
    queryKey: [...COLLEAGUES_KEY, user?.id, 'active'],
    queryFn: () => fetchOneOnOneColleagues(user!.id, { hidden: false }),
    enabled: Boolean(user?.id),
  });

  const { data: corrections = [] } = useQuery({
    queryKey: EXCEPTIONS_KEY,
    queryFn: fetchFollowExceptions,
  });

  const {
    data: preferences,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['meetingFollowPreferences'],
    queryFn: fetchFollowPreferences,
  });

  /* What was just ticked, until the server has been re-read and agrees. */
  const [inFlight, setInFlight] = useState<Record<string, boolean>>({});

  const tick = useMutation({
    mutationFn: async ({ colleague, follow }: TickChange) => {
      if (follow) {
        await addFollowException({
          target_kind: 'person',
          email: colleague.email,
          follow: true,
          record: null,
        });
        return;
      }

      const correction = followCorrectionFor(colleague, corrections);
      if (correction) await removeFollowException(correction.id);
    },
    /*
     * Settled rather than successful: dropping the optimistic tick after the
     * re-read is what reverts a pick the server refused, with no second path.
     */
    onSettled: async (_result, _error, { colleague }) => {
      await queryClient.invalidateQueries({ queryKey: EXCEPTIONS_KEY });
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
      /* Shared with the Team page, which shows whether each person is followed. */
      await queryClient.invalidateQueries({ queryKey: COLLEAGUES_KEY });
      setInFlight((current) =>
        Object.fromEntries(Object.entries(current).filter(([id]) => id !== colleague.id)),
      );
    },
  });

  const save = useMutation({
    mutationFn: saveFollowPreferences,
    onSuccess: async (saved) => {
      /* The server decides what was stored, so the page follows it. */
      queryClient.setQueryData(['meetingFollowPreferences'], saved);
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });

  /* Optional and never pre-filled: an empty answer stores nothing. */
  const [manager, setManager] = useState<string | null>(null);
  const managerAnswer = useManagerAnswer();

  const top = topColleagues(colleagues.data);
  const askManager =
    !sync.pending &&
    user?.manager_question_answered_at === null &&
    (colleagues.data ?? []).length > 0 &&
    !(colleagues.data ?? []).some((colleague) => colleague.relationship === 'manager');

  if (isPending) return null;
  if (isError) {
    return (
      <p className='text-base text-ink-muted'>{m.onboarding_preferences_load_failed()}</p>
    );
  }

  /*
   * The kinds are answered here too, and answered off: this branch follows the
   * people the user picked, so a kind left on would quietly add everybody else.
   */
  const saveChoice = (notetaker: boolean) =>
    save.mutate({
      follow_one_on_ones: false,
      follow_team_meetings: false,
      follow_all: false,
      notetaker,
    });

  /*
   * Recommended on for somebody who never chose. Only shown, not stored: the
   * choice is saved when the user confirms or flips it, so opening the step
   * changes nothing for anybody.
   */
  const notetaker = preferences.configured ? preferences.notetaker : true;

  const ticked = (colleague: OneOnOneColleagueItem) =>
    inFlight[colleague.id] ?? followCorrectionFor(colleague, corrections) !== undefined;

  const toggle = (colleague: OneOnOneColleagueItem, follow: boolean) => {
    setInFlight((current) => ({ ...current, [colleague.id]: follow }));
    tick.mutate({ colleague, follow });
  };

  const confirm = () => {
    saveChoice(notetaker);
    /*
     * Not awaited, like the switch above: the optional question never holds up
     * finishing. Should it fail, it simply stays unanswered and the app asks later.
     */
    if (askManager && manager !== null && user) {
      managerAnswer.mutate({ userId: user.id, choice: manager });
    }
    void navigate({ to: '/' });
  };

  return (
    <Card padding='none' className='mx-auto w-full max-w-120 overflow-hidden'>
      <div className='min-w-0 flex-1 px-9 py-10'>
        <SaveStatus
          pending={save.isPending || tick.isPending}
          failed={save.isError || tick.isError}
        />

        <h1 className='mb-5 text-xl font-semibold tracking-tight'>
          {m.onboarding_colleagues_title()}
        </h1>

        {sync.pending ? (
          <p className='text-sm text-ink-muted'>{m.onboarding_week_syncing()}</p>
        ) : (
          <ColleaguesToPick colleagues={top} ticked={ticked} onTick={toggle} />
        )}

        <div className='mt-4.5 flex items-center justify-between gap-4 rounded-md border border-line bg-fill-faint px-4 py-3.5'>
          <div className='flex flex-col items-start gap-1'>
            <p className='flex flex-wrap items-center gap-2 text-base font-bold text-ink'>
              {m.onboarding_notetaker()}
              <Badge tone='accent'>{m.onboarding_notetaker_recommended()}</Badge>
            </p>
            <a
              href={helpUrl(getLocale(), 'managers/notetaker/')}
              target='_blank'
              rel='noopener noreferrer'
              className={buttonVariants({ variant: 'link', size: 'sm' })}
            >
              {m.onboarding_notetaker_help()}
            </a>
          </div>
          <Switch
            aria-label={m.onboarding_notetaker()}
            checked={notetaker}
            onCheckedChange={saveChoice}
          />
        </div>

        <p className='mt-4 text-sm text-ink-muted'>
          {m.onboarding_colleagues_agenda_note()}
        </p>

        {askManager && (
          <div className='mt-6 flex flex-col gap-2.5 border-t border-fill pt-5'>
            <div className='flex flex-col gap-1'>
              <p className='flex items-baseline gap-2 text-base font-bold text-ink'>
                {m.manager_question_title()}
                <span className='text-sm font-medium text-ink-muted'>
                  {m.manager_question_optional()}
                </span>
              </p>
              <p className='text-sm text-ink-muted'>
                {m.manager_question_onboarding_explanation()}
              </p>
            </div>
            <ManagerSelect
              colleagues={colleagues.data ?? []}
              value={manager}
              onValueChange={setManager}
            />
            {manager === PERSON_SELECT_NOT_LISTED && (
              <p className='text-sm text-ink-body'>
                {m.manager_question_not_listed_note()}
              </p>
            )}
          </div>
        )}

        <div className='mt-7 flex items-center gap-3'>
          {/* Never gated on a pick: somebody with nobody to add is still done. */}
          <Button variant='accent' shape='block' size='lg' onClick={confirm}>
            {m.onboarding_confirm()}
          </Button>
          <Button
            variant='ghost'
            shape='block'
            size='lg'
            onClick={() => void navigate({ to: '/onboarding/calendar' })}
          >
            {m.onboarding_back()}
          </Button>
        </div>
      </div>
    </Card>
  );
}

interface TickChange {
  colleague: OneOnOneColleagueItem;
  follow: boolean;
}

interface ColleaguesToPickProps {
  colleagues: readonly OneOnOneColleagueItem[];
  ticked: (colleague: OneOnOneColleagueItem) => boolean;
  onTick: (colleague: OneOnOneColleagueItem, follow: boolean) => void;
}

/*
 * The people to tick, or the line saying no 1:1 turned up. Never anybody met
 * only in group meetings: a tick follows a person's 1:1s, so it would do nothing.
 */
function ColleaguesToPick({ colleagues, ticked, onTick }: ColleaguesToPickProps) {
  if (colleagues.length === 0) {
    return <p className='text-sm text-ink-muted'>{m.onboarding_colleagues_empty()}</p>;
  }

  return (
    <div className='flex flex-col gap-2'>
      {colleagues.map((colleague) => (
        <ContactOption
          key={colleague.id}
          name={colleague.label}
          email={colleague.email}
          imageUrl={colleague.profile_image_url}
          checked={ticked(colleague)}
          onCheckedChange={(checked) => onTick(colleague, checked)}
        />
      ))}
    </div>
  );
}

/** The colleagues this user has the most 1:1s with, as the Team page knows them. */
function topColleagues(
  colleagues: OneOnOneColleagueItem[] | undefined,
): OneOnOneColleagueItem[] {
  return [...(colleagues ?? [])]
    .sort((left, right) => right.one_on_one_count - left.one_on_one_count)
    .slice(0, SHOWN);
}
